import { Inject, Injectable, Optional } from '@angular/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import {
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { FALLBACK_LOCALE, formatDateWithOptions, normalizeTimezone } from '../helpers/format-date';
import { IDateFormatOptions } from '../models/date-time-format-options.model';

/**
 * Service for formatting dates with timezone and locale support. See library README for more details
 * and usage examples.
 */
@Injectable({ providedIn: 'root' })
export class LocalizedDateService {
  constructor(
    @Optional() @Inject(I18NEXT_SERVICE)
    private i18next: ITranslationService | null,
  ) {}

  /**
   * Authenticated caller's user_profile.time_zone, pushed in via setProfileTimezone. Defaulted
   * into the formatting methods when the caller supplies no profileTimezone (e.g. the
   * LocalizedDatePipe path), so it wins over the America/Denver fallback when the browser zone
   * is invalid. The leaf lib intentionally holds only this string and imports no user/profile
   * type, so there is no circular dependency back onto the data-access/user libs.
   */
  private storedProfileTimezone?: string;

  /**
   * Store (or clear) the authenticated profile timezone. Authenticated surfaces call this on
   * user refresh with user_profile.time_zone, and with `undefined` on logout/null user.
   */
  setProfileTimezone(tz?: string): void {
    this.storedProfileTimezone = tz;
  }

  /**
   * Gets browser's timezone, normalized to a valid IANA zone. Privacy browsers and
   * crawlers report the non-IANA zone 'Etc/Unknown'; in that case this returns the
   * safe fallback ('America/Denver') rather than the raw browser value, so downstream
   * Intl and date-fns-tz calls cannot throw a RangeError. To prefer a profile timezone
   * over the America/Denver default, use formatDate/formatRelative/getTimezoneAbbrevation
   * and pass the profile zone (those methods consult the raw browser zone first).
   */
  getBrowserTimezone(): string {
    // Privacy browsers/crawlers report the non-IANA zone 'Etc/Unknown'; normalize it so
    // downstream Intl/date-fns-tz calls cannot throw a RangeError.
    return normalizeTimezone(this.getRawBrowserTimezone());
  }

  /**
   * Raw browser-reported zone, which may be the non-IANA 'Etc/Unknown' on privacy
   * browsers/crawlers. Used as the default `timezone` for the formatting methods so that
   * normalizeTimezone owns the single fallback decision with the caller's profileTimezone
   * in hand: when the browser cannot report a valid zone, a supplied profileTimezone wins
   * over the America/Denver default instead of being pre-empted by an eager normalization.
   */
  private getRawBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Gets browser's locale
   */
  getBrowserLocale(): string {
    return navigator.language || 'en-US';
  }

  /**
   * Map country code to locale
   * @param country - Country code (e.g., 'US', 'GB', 'FR')
   * @returns Locale code (e.g., 'en-US', 'en-GB', 'fr-FR')
   */
  mapCountryToLocale(country: string): string {
    const countryToLocaleMap: Record<string, string> = {
      US: 'en-US',
      GB: 'en-GB',
      CA: 'en-CA',
      AU: 'en-AU',
      FR: 'fr-FR',
      DE: 'de-DE',
      ES: 'es-ES',
      IT: 'it-IT',
      JP: 'ja-JP',
      CN: 'zh-CN',
      MX: 'es-MX',
      BR: 'pt-BR',
      NL: 'nl-NL',
      SE: 'sv-SE',
      NO: 'nb-NO',
      DK: 'da-DK',
      FI: 'fi-FI',
      PL: 'pl-PL',
      PT: 'pt-PT',
      RU: 'ru-RU',
      IN: 'en-IN',
      KR: 'ko-KR'
    };

    return countryToLocaleMap[country.toUpperCase()] || 'en-US';
  }

  /**
   * Format date with timezone and locale support
   * @param date - Date to format (Date object or string)
   * @param options - Formatting options
   */
  formatDate(date: Date | string, options: IDateFormatOptions = {}): string {
    const {
      timezone = this.getRawBrowserTimezone(),
      locale = this.getBrowserLocale(),
      format = 'medium',
      customFormat,
      profileTimezone = this.storedProfileTimezone
    } = options;

    // Convert to Date object if string
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return '';
    }

    return formatDateWithOptions(dateObj, format, locale, timezone, customFormat, profileTimezone);
  }

  /**
   * Format date relative to now (e.g., "2 hours ago", "in 3 days")
   * @param date - Date to format
   * @param options - Formatting options
   */
  formatRelative(date: Date | string, options: Omit<IDateFormatOptions, 'format'> = {}): string {
    const {
      timezone = this.getRawBrowserTimezone(),
      locale = this.i18next?.language || this.getBrowserLocale(),
      profileTimezone = this.storedProfileTimezone
    } = options;

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Validate date
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return '';
    }

    // Convert both dates to the same timezone for comparison. Normalize first so an
    // invalid/Etc-Unknown zone falls back safely instead of throwing a RangeError. A
    // supplied profileTimezone wins over the America/Denver default when the zone is invalid.
    const safeTimezone = normalizeTimezone(timezone, profileTimezone);
    const zonedDate = toZonedTime(dateObj, safeTimezone);
    const zonedNow = toZonedTime(new Date(), safeTimezone);

    // Calculate differences using date-fns
    const diffYears = differenceInYears(zonedDate, zonedNow);
    const diffMonths = differenceInMonths(zonedDate, zonedNow);
    const diffDays = differenceInDays(zonedDate, zonedNow);
    const diffHours = differenceInHours(zonedDate, zonedNow);
    const diffMinutes = differenceInMinutes(zonedDate, zonedNow);

    let rtf: Intl.RelativeTimeFormat;
    try {
      rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    } catch {
      rtf = new Intl.RelativeTimeFormat(FALLBACK_LOCALE, { numeric: 'auto' });
    }

    // Determine which unit to use
    if (Math.abs(diffYears) >= 1) {
      return rtf.format(diffYears, 'year');
    }
    if (Math.abs(diffMonths) >= 1) {
      return rtf.format(diffMonths, 'month');
    }
    if (Math.abs(diffDays) >= 1) {
      return rtf.format(diffDays, 'day');
    }
    if (Math.abs(diffHours) >= 1) {
      return rtf.format(diffHours, 'hour');
    }
    return rtf.format(diffMinutes, 'minute');
  }

  getTimezoneAbbrevation(
    date: Date | string,
    tz: string,
    profileTimezone: string | undefined = this.storedProfileTimezone
  ): string {
    // Normalize so an invalid/Etc-Unknown zone falls back to America/Denver instead of
    // throwing, matching the contract used by formatDate/formatRelative/getBrowserTimezone.
    // A supplied profileTimezone wins over the America/Denver default when tz is invalid.
    const timezone = normalizeTimezone(tz || this.getRawBrowserTimezone(), profileTimezone);
    const locale = this.getBrowserLocale();

    // Convert to Date object if string
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return '';
    }

    try {
      const parts = Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: 'short' })
        .formatToParts(dateObj);

      const tzPart = parts.find((part) => part.type === 'timeZoneName');
      if (!tzPart || typeof tzPart.value !== 'string') {
        return '';
      }

      return tzPart.value;
    } catch (error) {
      return '';
    }
  }

  getSupportedTimeZones(): string[] {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }

    // Intl.supportedValuesOf occasionally is not supported in older browsers
    // This is a fallback of some timezones that we support
    return [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Indiana/Indianapolis',
      'America/Phoenix',
      'America/Juneau',
      'Australia/Sydney',
      'Pacific/Honolulu',
      'Pacific/Auckland',
      'UTC',
    ];
  }
}
